/**
 * Accessibility Utility - Auto-fix missing alt text
 * Handles dynamically generated content (e.g., Builder.io) that may have accessibility issues
 */

(function () {
    'use strict';

    /**
     * Check if an image appears to be decorative based on context
     */
    function isLikelyDecorativeImage(img) {
        const src = img.src || '';
        const className = img.className || '';

        // Check for common patterns that indicate decorative images
        const decorativePatterns = [
            /1x1\.png/i,              // Tracking pixels
            /pixel/i,                 // Tracking/spacer pixels
            /spacer/i,                // Spacer images
            /divider/i,               // Divider graphics
            /icon/i,                  // Icon images (when paired with text)
            /shipping\.svg/i,         // Shipping icons
            /builder-pixel/i,         // Builder.io tracking pixels
        ];

        // Check if image matches decorative patterns
        const matchesPattern = decorativePatterns.some(pattern =>
            pattern.test(src) || pattern.test(className)
        );

        // Check if image has presentation-related attributes
        const hasPresentationRole = img.getAttribute('role') === 'presentation';
        const isAriaHidden = img.getAttribute('aria-hidden') === 'true';

        // Check if it's in a Builder.io component (often decorative)
        const inBuilderComponent = img.closest('[data-builder-component]') !== null;

        // Check if very small (likely decorative/tracking)
        const width = parseInt(img.getAttribute('width') || img.width || 0);
        const height = parseInt(img.getAttribute('height') || img.height || 0);
        const isVerySmall = (width > 0 && width <= 1) || (height > 0 && height <= 1);

        return matchesPattern || hasPresentationRole || isAriaHidden || isVerySmall;
    }

    /**
     * Fix missing alt attributes on images
     */
    function fixMissingAltText() {
        const images = document.querySelectorAll('img:not([alt])');
        let fixedCount = 0;
        let warningCount = 0;

        images.forEach(img => {
            // Skip if already processed
            if (img.hasAttribute('data-alt-fixed')) {
                return;
            }

            if (isLikelyDecorativeImage(img)) {
                // Add empty alt for decorative images
                img.setAttribute('alt', '');
                img.setAttribute('data-alt-fixed', 'decorative');
                fixedCount++;

                // Optionally add aria-hidden and role for better accessibility
                if (!img.hasAttribute('aria-hidden')) {
                    img.setAttribute('aria-hidden', 'true');
                }
                if (!img.hasAttribute('role')) {
                    img.setAttribute('role', 'presentation');
                }

                console.log('[A11y] Fixed decorative image:', img.src);
            } else {
                // Log warning for potentially meaningful images
                console.warn('[A11y] Image missing alt text (not auto-fixed):', img.src);
                img.setAttribute('data-alt-fixed', 'warning');
                warningCount++;
            }
        });

        if (fixedCount > 0 || warningCount > 0) {
            console.log(`[A11y] Fixed ${fixedCount} decorative images, ${warningCount} warnings`);
        }
    }

    /**
     * Ensure proper landmark structure
     */
    function ensureLandmarks() {
        // Ensure footer has proper contentinfo role
        const footers = document.querySelectorAll('footer:not([role])');
        footers.forEach(footer => {
            if (!footer.getAttribute('role')) {
                footer.setAttribute('role', 'contentinfo');
                console.log('[A11y] Added contentinfo role to footer');
            }
        });

        // Ensure main has proper main role
        const mains = document.querySelectorAll('main:not([role])');
        mains.forEach(main => {
            if (!main.getAttribute('role')) {
                main.setAttribute('role', 'main');
                console.log('[A11y] Added main role to main element');
            }
        });
    }

    /**
     * Initialize accessibility fixes
     */
    function init() {
        ensureLandmarks();
        // Run immediately on DOMContentLoaded
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fixMissingAltText);
        } else {
            fixMissingAltText();
        }

        // Re-run when new content is added (for dynamic content like Builder.io)
        const observer = new MutationObserver(function (mutations) {
            let shouldCheck = false;

            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                        if (node.tagName === 'IMG' || node.querySelector('img')) {
                            shouldCheck = true;
                        }
                    }
                });
            });

            if (shouldCheck) {
                fixMissingAltText();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Re-check landmarks when sections are added (e.g., Shopify editor)
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
            document.addEventListener('shopify:section:load', ensureLandmarks);
        }

        console.log('[A11y] Accessibility utility initialized');
    }

    // Initialize
    init();

})();
